import { RefObject, useEffect } from "react";

const CHAT_OFFSET_VAR = "--fourhub-chat-offset-bottom";

// Região do botão do chat a partir da base da janela: 16px de margem + 64px do botão.
const CHAT_AREA_BOTTOM = 16;
const CHAT_AREA_TOP = 80;

/**
 * Sobe o chat enquanto o elemento estiver na região do botão dele, no fim da
 * tela. Fora dessa região (ex.: barra no meio do conteúdo), o chat fica na
 * posição original.
 */
export const useChatOffset = (ref: RefObject<HTMLElement>) => {
  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const root = document.documentElement;
    // Só limpa a variável se foi este elemento que publicou, para não apagar a do BottomMenu.
    let published = false;

    const clear = () => {
      if (!published) return;
      root.style.removeProperty(CHAT_OFFSET_VAR);
      published = false;
    };

    const publish = () => {
      const { top, bottom } = element.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      const isOverChat =
        bottom > viewportHeight - CHAT_AREA_TOP &&
        top < viewportHeight - CHAT_AREA_BOTTOM;

      if (!isOverChat) {
        clear();
        return;
      }

      root.style.setProperty(
        CHAT_OFFSET_VAR,
        `${Math.max(0, Math.round(viewportHeight - top))}px`
      );
      published = true;
    };

    publish();

    const observer = new ResizeObserver(publish);
    observer.observe(element);
    observer.observe(document.body);
    // O conteúdo ao lado da barra (ex.: horários que carregam depois) a empurra
    // para baixo sem mudar o tamanho dela nem rolar a página.
    Array.from(element.parentElement?.children ?? []).forEach((sibling) =>
      observer.observe(sibling)
    );
    window.addEventListener("resize", publish);
    // capture: as telas rolam dentro de containers, e não só na janela.
    window.addEventListener("scroll", publish, true);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", publish);
      window.removeEventListener("scroll", publish, true);
      clear();
    };
  }, [ref]);
};
